/**
 * Los íconos, dibujados a mano y en una sola línea de HTML.
 *
 * SIN LIBRERÍA DE ÍCONOS a propósito. Traer una entera para usar cuatro
 * figuras son cientos de kilobytes que alguien descarga con los datos del
 * teléfono en la cancha. Cada uno de estos es un `<path>`; el archivo entero
 * pesa menos que el índice del paquete que reemplaza.
 *
 * TODOS LLEVAN `title` Y NO `aria-label` SUELTO. Un ícono sin nombre es un
 * botón mudo: quien usa lector de pantalla oye «enlace» y nada más. El texto
 * viaja desde afuera porque estas figuras se usan en nueve idiomas.
 *
 * `currentColor` en vez de un color fijo: así el ícono cambia con el tema
 * claro/oscuro sin que haya que acordarse de este archivo.
 */
import { DIVISION_CHEVRONS, type CategoryDivision } from '@tennisup/contracts';

interface IconProps {
  /** Lo que se lee en voz alta y aparece al dejar el puntero encima. */
  title: string;
  size?: number;
}

export function WhatsAppIcon({ title, size = 22 }: IconProps): JSX.Element {
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      fill="currentColor"
    >
      <title>{title}</title>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 1.82c2.16 0 4.19.84 5.72 2.37a8.04 8.04 0 0 1 2.37 5.72c0 4.46-3.63 8.09-8.09 8.09a8.2 8.2 0 0 1-4.13-1.13l-.3-.18-3.07.81.82-3-.19-.31a8.04 8.04 0 0 1-1.24-4.29c0-4.46 3.63-8.08 8.11-8.08Zm-3.2 4.3c-.15 0-.4.06-.61.29-.21.23-.8.78-.8 1.9 0 1.12.82 2.2.93 2.36.12.15 1.6 2.44 3.88 3.42.54.24.96.38 1.29.48.54.17 1.04.15 1.43.09.44-.07 1.34-.55 1.53-1.08.19-.53.19-.99.13-1.08-.05-.1-.2-.15-.42-.27-.23-.11-1.34-.66-1.55-.73-.2-.08-.36-.12-.51.11-.15.23-.58.73-.71.88-.13.15-.26.17-.49.06-.23-.12-.96-.36-1.83-1.13-.68-.6-1.13-1.35-1.27-1.58-.13-.23-.01-.35.1-.47.1-.1.23-.27.34-.4.12-.14.15-.23.23-.39.08-.15.04-.29-.02-.4-.06-.12-.51-1.23-.7-1.69-.19-.44-.38-.38-.51-.39h-.44Z" />
    </svg>
  );
}

/** Un comprobante que llegó como FOTO. */
export function PhotoIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm1 2v8.6l3.6-3.6 3 3 3.4-3.4L19 14.6V7H5Zm4 1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" />
    </svg>
  );
}

/** Un banco, con sus columnas: es una TRANSFERENCIA. */
export function BankIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M12 3 2 8v2h20V8L12 3Zm-7 9v6H3v2h18v-2h-2v-6h-2v6h-3v-6h-2v6H7v-6H5Z" />
    </svg>
  );
}

/** Una estrella: lo paga un PATROCINADOR. */
export function StarIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="m12 2 2.9 6.26 6.85.78-5.1 4.62 1.4 6.74L12 16.98l-6.05 3.42 1.4-6.74-5.1-4.62 6.85-.78L12 2Z" />
    </svg>
  );
}

/** Un visto: el club DIO POR BUENO el pago. */
export function CheckIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M9.55 17.6 4 12.05l1.42-1.41 4.13 4.13 9.03-9.02L20 7.17 9.55 17.6Z" />
    </svg>
  );
}

/** Una cruz: el club dijo que NO. */
export function CrossIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M18.3 5.71 12 12.01l-6.3-6.3-1.41 1.42 6.3 6.29-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.29-1.41-1.42Z" />
    </svg>
  );
}

/** Un comprobante que llegó como COMENTARIO escrito. */
/** Dos eslabones de cadena: es «un enlace», que es lo que se comparte acá. */
export function LinkIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M10.6 13.4a4 4 0 0 0 5.66 0l2.83-2.83a4 4 0 1 0-5.66-5.66l-1.41 1.42 1.41 1.41 1.42-1.41a2 2 0 1 1 2.83 2.83l-2.83 2.83a2 2 0 0 1-2.83 0Zm2.8-2.8a4 4 0 0 0-5.66 0l-2.83 2.83a4 4 0 1 0 5.66 5.66l1.41-1.42-1.41-1.41-1.42 1.41a2 2 0 1 1-2.83-2.83l2.83-2.83a2 2 0 0 1 2.83 0Z" />
    </svg>
  );
}

export function CommentIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-4 4v-4H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm3 4v2h10V8H7Zm0 4v2h7v-2H7Z" />
    </svg>
  );
}

/** Se inscribió y NO mandó nada todavía: el reloj corriendo. */
export function ClockIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 2a7 7 0 1 1 0 14 7 7 0 0 1 0-14Zm-1 2v5.4l4 2.4.9-1.5-3.4-2V7h-1.5Z" />
    </svg>
  );
}

/** Lo metió quien organiza, a mano. */
export function HandIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M15 4a2 2 0 0 1 2 2v4h1a3 3 0 0 1 3 3v3a6 6 0 0 1-6 6h-3a6 6 0 0 1-6-6v-1.6a2 2 0 0 1 3.4-1.4l.6.6V6a2 2 0 0 1 4 0V4h1Zm-2 2v6h-2V6a.5.5 0 0 0-1 0v8.8l-2-2a.5.5 0 0 0-.9.4V17a4.5 4.5 0 0 0 4.5 4.5h3A4.5 4.5 0 0 0 19.5 17v-3a1.5 1.5 0 0 0-1.5-1.5h-2.5V6a.5.5 0 0 0-1 0Z" />
    </svg>
  );
}

/** La ubicación tomada del GPS. */
export function PinIcon({ title, size = 18 }: IconProps): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={title} fill="currentColor">
      <title>{title}</title>
      <path d="M12 2a7 7 0 0 0-7 7c0 5.2 6.3 12.4 6.6 12.7a.5.5 0 0 0 .8 0C12.7 21.4 19 14.2 19 9a7 7 0 0 0-7-7Zm0 2a5 5 0 0 1 5 5c0 3.5-3.6 8.5-5 10.3C10.6 17.5 7 12.5 7 9a5 5 0 0 1 5-5Zm0 2.5A2.5 2.5 0 1 0 12 11a2.5 2.5 0 0 0 0-4.5Z" />
    </svg>
  );
}

/**
 * LOS ÍCONOS DE LAS PESTAÑAS, y por qué estos son DECORATIVOS.
 *
 * Cada pestaña lleva ícono Y palabra, uno al lado del otro. El ícono no
 * agrega información: repite la palabra en dibujo. Anunciarlo sería leer dos
 * veces lo mismo —«casa, Inicio»— así que van con `aria-hidden` y sin
 * `<title>`, que es exactamente lo contrario de los íconos de pago, donde el
 * dibujo ES el dato y no hay texto al lado.
 */
interface TabIconProps {
  /** Vacío = decorativo. Ver el comentario de arriba. */
  title: string;
  size?: number;
}

function TabIcon({ title, size = 22, d }: TabIconProps & { d: string }): JSX.Element {
  const decorativo = title === '';
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      {...(decorativo ? { 'aria-hidden': true } : { role: 'img', 'aria-label': title })}
    >
      {!decorativo && <title>{title}</title>}
      <path d={d} />
    </svg>
  );
}

/** Inicio. */
export function HomeIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M12 3.2 3 10.4V21h6.2v-6h5.6v6H21V10.4L12 3.2Zm0 2.56 7 5.6V19h-2.2v-6H7.2v6H5v-7.64l7-5.6Z"
    />
  );
}

/** Comunidades: tres personas. */
export function CommunityIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0-2a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm8.5 2a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0-2a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM9 12.5c-3 0-6 1.5-6 3.75V20h12v-3.75c0-2.25-3-3.75-6-3.75Zm0 2c2.5 0 4 1.1 4 1.75V18H5v-1.75c0-.65 1.5-1.75 4-1.75Zm8.5-1c-.8 0-1.55.1-2.2.3.8.8 1.2 1.75 1.2 2.7V20H21v-3.25c0-1.7-1.9-2.75-3.5-2.75Z"
    />
  );
}

/** Una persona: el perfil propio. */
export function PersonIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-2a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm0 3c-3.6 0-7 1.8-7 4.2V21h14v-3.8c0-2.4-3.4-4.2-7-4.2Zm0 2c3 0 5 1.4 5 2.2V19H7v-1.8c0-.8 2-2.2 5-2.2Z"
    />
  );
}

/** Torneos: la copa. */
export function TrophyIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M18 4V3H6v1H2.5v2.5A4.5 4.5 0 0 0 6.7 11a5.3 5.3 0 0 0 4.3 3.44V17H8v2h8v-2h-3v-2.56A5.3 5.3 0 0 0 17.3 11a4.5 4.5 0 0 0 4.2-4.5V4H18ZM6 8.9A2.5 2.5 0 0 1 4.5 6.5V6H6v2.9Zm10 .35A3.35 3.35 0 0 1 12.65 12h-1.3A3.35 3.35 0 0 1 8 8.65V5h8v4.25Zm3.5-2.75A2.5 2.5 0 0 1 18 8.9V6h1.5v.5Z"
    />
  );
}

/**
 * Retos: dos raquetas cruzadas.
 *
 * Cruzadas y no una sola, porque un reto es entre DOS. Una raqueta suelta
 * dice «jugar»; dos cruzadas dicen «vos contra mí», que es lo que la pestaña
 * hace. A 22 px lo único que se lee de una raqueta es el óvalo con el mango,
 * así que es exactamente eso y nada de cuerdas.
 */
export function ChallengeIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M7.5 3.5a4 4 0 1 1 0 8 4 4 0 1 1 0-8Zm0 1.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 1 0 0-4.8ZM16.5 3.5a4 4 0 1 1 0 8 4 4 0 1 1 0-8Zm0 1.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 1 0 0-4.8ZM8.92 10.48 15.72 17.28 17.28 15.72 10.48 8.92ZM13.52 8.92 6.72 15.72 8.28 17.28 15.08 10.48Z"
    />
  );
}

/**
 * Ir de primero en el ranking: la corona.
 *
 * TRES PUNTAS CON SU CUENTA ARRIBA Y LA BANDA MACIZA APARTE. Dibujada a 110 px
 * y mirada a 22, que es el tamaño al que sale en el perfil. Las cuentas son lo
 * que la hace corona: sin ellas, a 22 px las tres puntas se leen como una
 * montaña. Y la banda va separada del cuerpo —pegada, el conjunto se lee como
 * una copa—.
 */
export function CrownIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M12 4.6a1.5 1.5 0 0 1 1.28 2.28L15.6 10.8l3.7-2.6a1.5 1.5 0 1 1 1.3.76L18.8 17.2H5.2L3.4 8.96a1.5 1.5 0 1 1 1.3-.76l3.7 2.6 2.32-3.92A1.5 1.5 0 0 1 12 4.6Z M5.1 18.8h13.8V21H5.1Z"
    />
  );
}

/**
 * LA MEDALLA DEL FINALISTA Y DEL SEMIFINALISTA.
 *
 * Javier: «en los logros haz iconos para el segundo y tercero». Antes los tres
 * —campeón, finalista y semifinalista— salían con la MISMA copa, así que el
 * perfil de quien llegó a semis y el de quien ganó se leían igual de un
 * vistazo, que es el único vistazo que hay.
 *
 * LA COPA SE QUEDA SOLO PARA EL CAMPEÓN. Ganar un torneo es otra cosa que
 * llegar lejos, y el dibujo tiene que decirlo antes que el texto.
 *
 * UNA MEDALLA Y NO UNA COPA MÁS CHICA: a 22 px el tamaño no se compara —no hay
 * con qué— así que dos copas distintas se leen como la misma. Un disco colgado
 * de una cinta es otra silueta, y eso sí se distingue sin leer.
 *
 * EL NÚMERO VA ADENTRO, y es lo que separa la plata del bronce sin depender
 * del color. Javier eligió plata y bronce, pero el color solo no alcanza: hay
 * gente que no los distingue, y en el tema oscuro los dos tiran a gris. El 2 y
 * el 3 se leen a 22 px porque son un solo trazo grueso en el centro del disco,
 * que es la parte más despejada del dibujo.
 */
export function MedalIcon({
  place,
  title,
  size = 22,
}: IconProps & {
  /** 2 es plata y 3 es bronce. No hay medalla de 1: esa es la copa. */
  place: 2 | 3;
}): JSX.Element {
  return (
    <svg
      className={`icon medal-${place}`}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      fill="currentColor"
    >
      <title>{title}</title>
      {/* LA CINTA, en dos tiras que bajan del borde y se juntan en el disco.
          Va primero para que el disco la tape donde se cruzan. */}
      <path d="M7.6 2h3.2l1.7 3.9-2.9 1.1L7.6 2Zm8.8 0h-3.2l-1.7 3.9 2.9 1.1L16.4 2Z" />
      {/* EL DISCO, hueco: el número vive adentro y con el disco macizo habría
          que dibujarlo del color del fondo, que en tema claro y oscuro no es
          el mismo. */}
      <path d="M12 6.2a8.1 8.1 0 1 0 0 16.2 8.1 8.1 0 0 0 0-16.2Zm0 2.1a6 6 0 1 1 0 12 6 6 0 0 1 0-12Z" />
      {/* EL NÚMERO. Trazos rectos y gruesos, que es lo único que sobrevive a
          22 px adentro de un disco de 10 px de diámetro. */}
      {place === 2 ? (
        <path d="M9.5 12.4c0-1.5 1.1-2.5 2.6-2.5s2.5 1 2.5 2.3c0 .9-.5 1.6-1.4 2.4l-1.5 1.3h3v1.8H9.4v-1.5l3-2.6c.4-.4.6-.7.6-1 0-.4-.3-.7-.8-.7s-.9.3-.9.9H9.5Z" />
      ) : (
        <path d="M9.4 12.3c.1-1.4 1.2-2.4 2.7-2.4 1.5 0 2.6.9 2.6 2.1 0 .8-.4 1.4-1.2 1.7 1 .3 1.5 1 1.5 1.9 0 1.4-1.2 2.4-2.9 2.4-1.7 0-2.8-.9-2.9-2.4h1.9c0 .5.4.8 1 .8s1-.3 1-.8-.4-.7-1.1-.7h-.7v-1.5h.7c.6 0 1-.2 1-.7s-.3-.7-.9-.7-.9.3-.9.8H9.4Z" />
      )}
    </svg>
  );
}

/**
 * EL PUESTO EN EL RANKING DEL CLUB, para quien no va de primero.
 *
 * Javier: «también anota la posición que están en el club con otro icono para
 * el resto de los integrantes». La corona es del primero y sigue siéndolo;
 * esto es para el resto, que hasta ahora no tenía nada — su perfil no decía en
 * qué puesto va aunque el dato existiera en el ranking del club.
 *
 * UNA LISTA CON UNA FILA MARCADA, y no un número suelto. El número ya está en
 * el texto del logro («3.º en B»); repetirlo adentro del icono sería decir dos
 * veces lo mismo y obligaría a un dibujo distinto por cada puesto. Tres
 * renglones con el del medio resaltado dicen «una tabla, y vos estás ahí», que
 * es lo que el logro significa, y sirve igual para el 3.º que para el 27.º.
 */
export function RankPositionIcon({ title, size = 22 }: IconProps): JSX.Element {
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      fill="currentColor"
    >
      <title>{title}</title>
      {/* Los dos renglones de arriba y abajo, apagados por ser más finos. */}
      <path d="M4 5.5h16v2H4ZM4 16.5h16v2H4Z" opacity="0.45" />
      {/* EL DEL MEDIO ES EL SUYO: más alto y con la marca a la izquierda. Sin
          la marca, a 22 px las tres barras se leen como un menú. */}
      <path d="M2 10.4h2.6v3.2H2ZM6 10.4h14v3.2H6Z" />
    </svg>
  );
}

/**
 * LA DIVISIÓN: un escudo con uno, dos o tres galones.
 *
 * QUÉ SE ELIGIÓ Y POR QUÉ. Se dibujaron tres familias —medalla con puntos,
 * escudo con galones, estrellas sueltas— a 96 px y se miraron a 22 y a 18, que
 * es el tamaño al que sale. Los puntos adentro de la medalla se vuelven una
 * mancha; las estrellas de tres se amontonan sin nada que las contenga; los
 * galones se CUENTAN, que es justo lo que hay que poder hacer con ellos.
 *
 * EL NÚMERO DE GALONES SALE DEL CONTRATO y no de un `if` acá: «tres galones es
 * el oro» es parte de lo que la división es, y dos pantallas que lo dibujen
 * distinto son un perfil que se contradice.
 *
 * Y VA CENTRADO EN EL ESCUDO. Con el grupo pegado abajo —que fue el primer
 * intento— el bronce queda con medio escudo vacío arriba y se lee como algo a
 * medio dibujar.
 */
export function DivisionIcon({
  division,
  title,
  size = 18,
}: TabIconProps & { division: CategoryDivision }): JSX.Element {
  const galones = DIVISION_CHEVRONS[division] ?? 1;
  // Los vértices, de arriba hacia abajo, ya centrados para cada cantidad.
  const alturas = galones === 1 ? [10.4] : galones === 2 ? [8.8, 12] : [7.2, 10.4, 13.6];
  const d =
    ESCUDO + alturas.map((y) => `M7.5 ${y + 2.6} 12 ${y}l4.5 2.6-1 1.7-3.5-2-3.5 2Z`).join('');
  return <TabIcon title={title} size={size} d={d} />;
}

/** El contorno del escudo. Igual para las tres: lo que cambia son los galones. */
const ESCUDO =
  'M12 2 21 5v7.4c0 4.3-3.6 7.6-9 9.6-5.4-2-9-5.3-9-9.6V5Zm0 2.1L5 6.4v6c0 3 2.5 5.6 7 7.3 4.5-1.7 7-4.3 7-7.3v-6Z';

/** Más: los tres puntos, que es como se dice «lo demás» en un teléfono. */
export function DotsIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M6 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"
    />
  );
}

/**
 * Salir de la cuenta: la puerta con la flecha que sale.
 *
 * Decorativo, como los de las pestañas: al lado dice «Cerrar sesión» y
 * anunciar el dibujo sería leer dos veces lo mismo.
 */
export function SignOutIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M10 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h4v-2H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4V3Zm5.3 4.3-1.4 1.4L16.2 11H9v2h7.2l-2.3 2.3 1.4 1.4L20 12l-4.7-4.7Z"
    />
  );
}

/** El idioma: el globo con sus meridianos. */
export function GlobeIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.9 9h-3a15.6 15.6 0 0 0-1.3-5.6A8 8 0 0 1 18.9 11ZM12 4.2c.8 1 1.7 3 1.9 6.8h-3.8c.2-3.8 1.1-5.8 1.9-6.8ZM5.1 11a8 8 0 0 1 4.3-5.6A15.6 15.6 0 0 0 8.1 11h-3Zm0 2h3a15.6 15.6 0 0 0 1.3 5.6A8 8 0 0 1 5.1 13ZM12 19.8c-.8-1-1.7-3-1.9-6.8h3.8c-.2 3.8-1.1 5.8-1.9 6.8Zm2.6-1.2a15.6 15.6 0 0 0 1.3-5.6h3a8 8 0 0 1-4.3 5.6Z"
    />
  );
}

/** El tema: el círculo con media mitad llena, que es como se dibuja siempre. */
export function ThemeIcon(props: TabIconProps): JSX.Element {
  return (
    <TabIcon
      {...props}
      d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 2v16a8 8 0 0 1 0-16Z"
    />
  );
}
